/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { fg } from '@confluence/feature-gating';

import type {
	useTrackTitleRedactionRenderedRedactionsCountQuery as useTrackTitleRedactionRenderedRedactionsCountQuery$data,
	useTrackTitleRedactionRenderedRedactionsCountQueryVariables as useTrackTitleRedactionRenderedRedactionsCountQuery$variables,
} from './__types__/useTrackTitleRedactionRenderedRedactionsCountQuery';
import { useRedactionsTourActions } from './useRedactionsTourState';

export const TITLE_REDACTION_STRING = '████';

export const doesTitleContainRedactionString = (title?: string) => {
	if (!title) {
		return false;
	}
	return title.includes(TITLE_REDACTION_STRING);
};

type UseTrackTitleRedactionRenderedProps = {
	contentId: string;
	title?: string;
};

export const useTrackTitleRedactionRendered = ({
	contentId,
	title,
}: UseTrackTitleRedactionRenderedProps) => {
	const { setTitleRedactionRenderedForContentId } = useRedactionsTourActions();

	const titleMaybeHasRedaction = useMemo(() => doesTitleContainRedactionString(title), [title]);

	const { data, error: redactionsCountError } = useQuery<
		useTrackTitleRedactionRenderedRedactionsCountQuery$data,
		useTrackTitleRedactionRenderedRedactionsCountQuery$variables
	>(
		gql`
			query useTrackTitleRedactionRenderedRedactionsCountQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					redactionMetadataCount
				}
			}
		`,
		{
			skip: !titleMaybeHasRedaction || !fg('dlp_cc-redactions-tour'),
			variables: {
				contentId,
			},
			errorPolicy: 'all',
		},
	);

	// handle 4xx errors
	useEffect(() => {
		if (redactionsCountError && isStatusCodeError(redactionsCountError, '404')) {
			markErrorAsHandled(redactionsCountError);
		}
	}, [redactionsCountError]);

	const redactionsCount = data?.singleContent?.redactionMetadataCount ?? 0;

	useEffect(() => {
		if (contentId && titleMaybeHasRedaction && redactionsCount > 0) {
			setTitleRedactionRenderedForContentId(contentId);
		}
		return () => {
			// Cleanup function to reset the title redaction state
			setTitleRedactionRenderedForContentId(null);
		};
	}, [contentId, titleMaybeHasRedaction, redactionsCount, setTitleRedactionRenderedForContentId]);
};
